import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Square, RotateCcw, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 30;

export default function VoiceRecorder({ onSend, onCancel, sending }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stop();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Mic blocked. Allow access and retry.");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
  };

  const send = async () => {
    if (!audioBlob) return;
    // Convert blob → base64 data URL
    const reader = new FileReader();
    reader.onload = () => {
      onSend(reader.result);
    };
    reader.readAsDataURL(audioBlob);
  };

  const pct = Math.min(100, (seconds / MAX_SECONDS) * 100);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <label className="block text-xs font-bold text-[#C9C7D9] ml-1 uppercase tracking-[0.2em]">
        Voice answer <span className="text-white/40 normal-case tracking-normal">· max {MAX_SECONDS}s</span>
      </label>

      <div className="card-glow rounded-2xl p-5 flex flex-col items-center gap-4">
        {!audioBlob && !recording && (
          <button
            onClick={start}
            type="button"
            className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center glow-pink active:scale-95 hover:scale-105 transition"
            data-testid="record-start-button"
            aria-label="Start recording"
          >
            <Mic className="w-9 h-9 text-white" />
          </button>
        )}

        {recording && (
          <>
            <button
              onClick={stop}
              type="button"
              className="w-20 h-20 rounded-full bg-[#FF4D8D] flex items-center justify-center active:scale-95 transition"
              style={{ boxShadow: "0 0 30px rgba(255,77,141,0.8), 0 0 60px rgba(255,77,141,0.4)", animation: "pulse-glow 1.2s ease-in-out infinite" }}
              data-testid="record-stop-button"
              aria-label="Stop recording"
            >
              <Square className="w-7 h-7 text-white" fill="white" />
            </button>
            <p className="text-white font-display-tight text-xl font-bold tabular-nums">
              {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
            </p>
            <div className="w-full h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] transition-[width] duration-[1000ms] ease-linear" style={{ width: `${pct}%` }} />
            </div>
          </>
        )}

        {audioBlob && !recording && (
          <>
            <audio src={audioUrl} controls className="w-full" data-testid="voice-preview-player" />
            <div className="grid grid-cols-2 gap-3 w-full">
              <button
                onClick={reset}
                type="button"
                disabled={sending}
                className="bg-white/5 border border-white/10 text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-white/10 active:scale-95 disabled:opacity-50 transition"
                data-testid="record-redo-button"
              >
                <RotateCcw className="w-4 h-4" /> Redo
              </button>
              <button
                onClick={send}
                type="button"
                disabled={sending}
                className="btn-gradient rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95"
                data-testid="record-send-button"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Your turn 👀
              </button>
            </div>
          </>
        )}

        {!recording && !audioBlob && (
          <p className="text-xs text-[#C9C7D9] font-semibold">Tap the mic to start recording</p>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        disabled={sending || recording}
        className="w-full bg-white/5 border border-white/10 text-white rounded-full py-3 font-bold hover:bg-white/10 disabled:opacity-50 transition"
        data-testid="record-cancel-button"
      >
        Cancel
      </button>
    </motion.div>
  );
}
